package de.xtl.retroguide.question.scenes

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.layout.layout
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.protocol.AnswerCategory
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.roundToInt
import kotlin.math.sin

private val Water = listOf(Color(0xFF4DD0E1), Color(0xFF00838F))
private val Sand = Color(0xFFFFE0B2)
private val StarBody = Color(0xFFFF7043)
private val StarDots = Color(0xFFFFCCBC)

private const val CENTER_Y = .55f
private const val MIN_ARM = .14f
private const val MAX_ARM = .34f
private const val LABEL_RADIUS = .45f

/** Starfish whose five arms grow with the answers of Start, Stop, Continue, More and Less. */
@Composable
internal fun StarfishScene(counts: Map<AnswerCategory, Int>, modifier: Modifier = Modifier) {
    val arms = listOf(
        AnswerCategory.START,
        AnswerCategory.STOP,
        AnswerCategory.CONTINUE,
        AnswerCategory.MORE,
        AnswerCategory.LESS,
    )
    val maxCount = arms.maxOf { counts[it] ?: 0 }.coerceAtLeast(1)
    val lengths = arms.map { arm ->
        val target = MIN_ARM + (MAX_ARM - MIN_ARM) * (counts[arm] ?: 0) / maxCount
        val length = remember { Animatable(MIN_ARM * .5f) }
        LaunchedEffect(target) {
            length.animateTo(target, spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessVeryLow))
        }
        length
    }
    val breathe by rememberInfiniteTransition().animateFloat(
        initialValue = .97f,
        targetValue = 1.03f,
        animationSpec = infiniteRepeatable(tween(2_400, easing = FastOutSlowInEasing), RepeatMode.Reverse),
    )
    BoxWithConstraints(modifier.fillMaxWidth().aspectRatio(4f / 3f).clip(RoundedCornerShape(24.dp))) {
        val width = constraints.maxWidth
        val height = constraints.maxHeight
        Canvas(Modifier.fillMaxSize()) {
            drawRect(Brush.verticalGradient(Water))
            drawSeabed()
            drawStarfish(
                center = Offset(size.width / 2, size.height * CENTER_Y),
                lengths = lengths.map { it.value * size.height * breathe },
            )
        }
        arms.forEachIndexed { index, arm ->
            val angle = armAngle(index)
            val x = width / 2f + cos(angle) * height * LABEL_RADIUS * 1.25f
            val y = height * CENTER_Y + sin(angle) * height * LABEL_RADIUS
            SceneBadge(
                arm,
                counts[arm] ?: 0,
                Modifier.layout { measurable, layoutConstraints ->
                    val placeable = measurable.measure(layoutConstraints.copy(minWidth = 0, minHeight = 0))
                    layout(placeable.width, placeable.height) {
                        placeable.place(
                            (x - placeable.width / 2f).roundToInt().coerceIn(8, (width - placeable.width - 8).coerceAtLeast(8)),
                            (y - placeable.height / 2f).roundToInt().coerceIn(8, (height - placeable.height - 8).coerceAtLeast(8)),
                        )
                    }
                },
            )
        }
    }
}

private fun armAngle(index: Int) = (-90f + index * 72f) * PI.toFloat() / 180f

private fun DrawScope.drawSeabed() {
    val path = Path().apply {
        moveTo(0f, size.height * .86f)
        quadraticTo(size.width * .3f, size.height * .8f, size.width * .55f, size.height * .87f)
        quadraticTo(size.width * .8f, size.height * .93f, size.width, size.height * .84f)
        lineTo(size.width, size.height)
        lineTo(0f, size.height)
        close()
    }
    drawPath(path, Sand)
    listOf(.1f to .3f, .85f to .22f, .7f to .62f, .2f to .7f).forEach { (x, y) ->
        drawCircle(Color.White.copy(alpha = .35f), size.height * .015f, Offset(size.width * x, size.height * y))
    }
}

private fun DrawScope.drawStarfish(center: Offset, lengths: List<Float>) {
    val armWidth = size.height * .12f
    val star = Path()
    lengths.forEachIndexed { index, length ->
        val angle = armAngle(index)
        val previous = armAngle(index) - 36f * PI.toFloat() / 180f
        val tip = center + Offset(cos(angle), sin(angle)) * length
        val valley = center + Offset(cos(previous), sin(previous)) * (armWidth * .75f)
        if (index == 0) star.moveTo(valley.x, valley.y) else star.lineTo(valley.x, valley.y)
        val side = Offset(-sin(angle), cos(angle)) * (armWidth * .35f)
        star.quadraticTo(tip.x - side.x * 1.4f, tip.y - side.y * 1.4f, tip.x, tip.y)
        val next = armAngle(index) + 36f * PI.toFloat() / 180f
        val nextValley = center + Offset(cos(next), sin(next)) * (armWidth * .75f)
        star.quadraticTo(tip.x + side.x * 1.4f, tip.y + side.y * 1.4f, nextValley.x, nextValley.y)
    }
    star.close()
    drawPath(star, StarBody)
    lengths.forEachIndexed { index, length ->
        val angle = armAngle(index)
        var distance = armWidth * .8f
        while (distance < length - armWidth * .3f) {
            drawCircle(StarDots, armWidth * .09f, center + Offset(cos(angle), sin(angle)) * distance)
            distance += armWidth * .5f
        }
    }
    drawCircle(Color(0xFFFF8A65), armWidth * .55f, center)
    drawCircle(StarDots, armWidth * .14f, center)
}
